// Fully refactored Layout component using MUI and styled-components with Popper for Settings
import React, { useEffect, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import {
  Box,
  IconButton,
  Button,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Tooltip,
  Switch,
  Badge,
  Popper,
  ClickAwayListener,
  Paper,
  ButtonBase,
} from '@mui/material';

import { Outlet, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import api from '../api/axios';
import { ReactComponent as MenuOutline } from '../assets/images/menu-outline.svg';
import { ReactComponent as TokenIcon } from '../assets/images/token.svg';
import chatboxOutline from '../assets/images/chatbox-outline.svg';
import ChartDetailIcon from '../assets/images/chartDetail.svg';
import Idea from '../assets/images/idea.svg';
import LogoDark from '../assets/images/logo_dark.svg';
import LogoLight from '../assets/images/logo_light.svg';
import { useTheme } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import { updateVisitedUserDetails } from '../redux/detailsSlice';
import PriorityHighRoundedIcon from '@mui/icons-material/PriorityHighRounded';
import useMediaQuery from '@mui/material/useMediaQuery';
import SettingsMenu from './SettingsMenu';
import { useAuth } from '../context/AuthContext';
import HowItWorksPopup from './HowItWorksPopup';
import posthog from 'posthog-js';
import Chat from '../Pages/Chat';
import WelcomeAstrologyDialog from './WelcomeAstrologyDialog';
import AuthPopup from './AuthPopup';
import { FREE_QUESTION } from '../const';

const Layout = () => {
  const modalRef = useRef(null);
  const isLoginRef = useRef(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsAnchor, setSettingsAnchor] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [email, setEmail] = useState('');
  const [userTokens, setUserTokens] = useState(0);
  const { isLogin, logout } = useAuth();

  useEffect(() => {
    isLoginRef.current = isLogin;
  }, [isLogin]);
  const [exclamationVisible, setExclamationVisible] = useState(false);
  const [openAuthPopup, setOpenAuthPopup] = React.useState(false);
  const [openAstrologyDialog, setOpenAstrologyDialog] = useState(() => {
    return isLogin ? false : true;
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setShowSettings(false);
      }
    };
    if (showSettings) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSettings]);

  useEffect(() => {
    if (!isLogin) return;
    const token = localStorage.getItem('token');
    if (token) {
      const decoded = jwtDecode(token);
      const id = decoded.email;
      axios.get(`${api}/user/details/${id}`).then((res) => {
        setEmail(res.data[0].email);
        setUserTokens(res.data[0].tokens || 0);
        posthog.identify(res.data[0].email, { name: res.data[0].name });
      });
    }
  }, [messages, selectedChatId]);

  useEffect(() => {
    if (!isLogin) return;
    axios
      .get(`${api}/chat/chat_history`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      })
      .then((res) => {
        setChatHistory(res.data);
      });
  }, []);

  const handleLogout = () => {
    posthog.reset();
    logout();
    dispatch(updateVisitedUserDetails(false));
    navigate('/login', { replace: true });
  };

  // Load chat history
  useEffect(() => {
    if (!isLogin) return;
    axios
      .get(`${api}/chat/chat_history`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      })
      .then((res) => {
        setChatHistory(res.data);
      });
  }, []);

  const loadingChatIdRef = useRef(null);
  const optimisticChatIdRef = useRef(null);

  // Track if pending question has been processed after login to prevent multiple calls
  const pendingQuestionProcessedRef = useRef(false);

  // Load selected chat
  useEffect(() => {
    if (!isLogin) return;
    // If there's a pending question to replay, skip loading messages to avoid
    // a race condition where the GET response overwrites the messages that
    // sendMessage will set after the POST completes
    if (sessionStorage.getItem('pendingQuestion')) {
      return;
    }
    if (selectedChatId) {
      // If sendMessage just set this chatId optimistically, don't overwrite until backend responds
      if (optimisticChatIdRef.current === selectedChatId) {
        optimisticChatIdRef.current = null;
        return;
      }
      loadingChatIdRef.current = selectedChatId;
      axios
        .get(`${api}/chat/chat/${selectedChatId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        })
        .then((res) => {
          // Only apply if no newer optimistic update happened
          if (loadingChatIdRef.current === selectedChatId) {
            setMessages(res.data ?? []);
          }
        });
    } else {
      const newId = crypto.randomUUID();
      setSelectedChatId(newId);
    }
  }, [selectedChatId]);

  const sendMessage = async (confirmTokens = false, questionParam = null) => {
    const userInput = questionParam ?? input;
    console.log('Debug: sendMessage called with:', {
      confirmTokens,
      questionParam,
      userInput,
      isThinking,
    });
    posthog.capture('chat_message_sent', {
        confirm_tokens: confirmTokens,
        question_param: questionParam,
        user_input: userInput,
      });
    if (!isLoginRef.current) {
      setOpenAuthPopup(true);
      return;
    }
    if (isThinking) return;
    if (!userInput.trim()) return;

    setIsThinking(true);

    let chatId = selectedChatId;

    // 1. Create new chatId if this is the first message and wait for it to stabilize
    if (!chatId) {
      chatId = crypto.randomUUID();
      setSelectedChatId(chatId);
      // Wait for React to flush state and the selector effect to run
      await new Promise((resolve) => setTimeout(resolve, 150));
    }

    // 2. Add user message locally and prevent loader from overwriting
    let newMessages;
    if (!confirmTokens) {
      optimisticChatIdRef.current = chatId;
      newMessages = [
        ...messages,
        {
          role: 'user',
          content: userInput,
          timestamp: new Date().toISOString(),
        },
      ];
      setMessages(newMessages);
      setInput('');
    } else {
      newMessages = messages; // Use existing messages for confirmation
    }

    // 3. Call Node server for assistant response
    try {
      const pendingQuestion = sessionStorage.getItem('pendingQuestion');
      let freeQuestion = false;
      if (pendingQuestion) {
        freeQuestion = true;
        sessionStorage.removeItem('pendingQuestion');
      }
      const res = await axios.post(
        `${api}/chat/getResponse`,
        {
          chat_id: chatId,
          messages: newMessages,
          confirmTokens: confirmTokens,
          freeQuestion: freeQuestion,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      console.log('Debug: Response from backend:', res.data);

      // Handle confirmation response
      if (res.data.requiresConfirmation) {
        const confirmationMessage = {
          role: 'assistant',
          content: res.data.message,
          timestamp: new Date().toISOString(),
          requiresConfirmation: true,
          userQuestion: res.data.userQuestion,
          tokensRequired: res.data.tokensRequired,
          currentTokens: res.data.currentTokens,
        };
        setMessages([...newMessages, confirmationMessage]);
        setIsThinking(false);
        return;
      }

      setIsThinking(false);
      setMessages(res.data.messages);

      // 5. Refresh chat history list if this was a new chat
      if (!chatHistory.find((chat) => chat.id === chatId)) {
        const updated = await axios.get(`${api}/chat/chat_history`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        setChatHistory(updated.data);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      posthog.captureException(error, {
        extra: { context: 'chat_message_sent' },
      });
      const updatedMessages = [
        ...newMessages,
        {
          role: 'assistant',
          content: error?.response?.data?.error || 'Error processing request',
          timestamp: new Date().toISOString(),
          isError: true,
        },
      ];
      setMessages(updatedMessages);
    } finally {
      setIsThinking(false);
    }
  };

  // Use a ref to ensure the pending question is only processed once after login
  useEffect(() => {
    if (!isLogin) return;
    if (pendingQuestionProcessedRef.current) return;

    const pendingQuestion = sessionStorage.getItem('pendingQuestion');

    if (pendingQuestion) {
      pendingQuestionProcessedRef.current = true;
      setInput(FREE_QUESTION);
      // Use a small timeout to ensure state is settled before calling sendMessage
      // NOTE: sendMessage itself reads and clears pendingQuestion from sessionStorage for freeQuestion logic
      setTimeout(() => sendMessage(false, FREE_QUESTION), 0);
    }
  }, [isLogin]);

  const handleNewChat = () => {
    const newId = crypto.randomUUID();
    setSelectedChatId(newId);
    setMessages([]);
    posthog.capture('new_chat_started');
    if (isMobile) setSidebarOpen(false);
  };

  const [isThinking, setIsThinking] = useState(false);

  const handleTokenConfirmation = async (accepted) => {
    console.log('Debug: handleTokenConfirmation called with:', {
      accepted,
      messages,
    });
    if (!accepted) {
      posthog.capture('token_usage_rejected');
    }
    if (accepted) {
      posthog.capture('token_usage_confirmed');
      setIsThinking(true);

      // Remove the confirmation message first
      const filteredMessages = messages.filter(
        (msg) => !msg.requiresConfirmation
      );
      setMessages(filteredMessages);

      // Call the backend with confirmation
      let chatId = selectedChatId;
      if (!chatId) {
        chatId = crypto.randomUUID();
        setSelectedChatId(chatId);
      }

      try {
        const res = await axios.post(
          `${api}/chat/getResponse`,
          {
            chat_id: chatId,
            messages: filteredMessages,
            confirmTokens: true,
            freeQuestion: false,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          }
        );

        setMessages(res.data.messages);

        // Refresh chat history list if this was a new chat
        if (!chatHistory.find((chat) => chat.id === chatId)) {
          const updated = await axios.get(`${api}/chat/chat_history`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          });
          setChatHistory(updated.data);
        }
      } catch (error) {
        console.error('Error confirming tokens:', error);
        const updatedMessages = [
          ...filteredMessages,
          {
            role: 'assistant',
            content: error?.response?.data?.error || 'Error processing request',
            timestamp: new Date().toISOString(),
            isError: true,
          },
        ];
        setMessages(updatedMessages);
      } finally {
        setIsThinking(false);
      }
    } else {
      // Remove the confirmation message and user message from the chat
      const filteredMessages = messages.filter(
        (msg) => !msg.requiresConfirmation
      );
      // Also remove the last user message since they cancelled
      const finalMessages = filteredMessages.slice(0, -1);
      setMessages(finalMessages);
    }
  };

  const handleSettingsClick = (event) => {
    setSettingsAnchor(event.currentTarget);
    setShowSettings((prev) => !prev);
  };

  const handleCloseSettings = () => {
    setShowSettings(false);
    setSettingsAnchor(null);
  };

  const handleLike = async (messageId, like) => {
    try {
      if (messageId == null) throw new Error('Message ID is null');
      if (messages == null) throw new Error('Messages are null');
      const newMessages = messages.map((msg) =>
        msg._id === messageId ? { ...msg, likes: like } : msg
      );
      await axios.post(
        `${api}/chat/chat/${selectedChatId}`,
        { messages: newMessages },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        }
      );
      setMessages(newMessages);
    } catch (error) {
      console.error('Error liking message:', error);
    }
  };

  const listItemSlotProps = {
    primary: {
      style: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontSize: '14px',
      },
    },
  };

  let localStorageDarkModePreference = localStorage.getItem('prefersDarkMode');
  if (localStorageDarkModePreference != null)
    localStorageDarkModePreference = localStorageDarkModePreference === 'true';
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const [darkMode, setDarkMode] = useState(
    localStorageDarkModePreference ?? prefersDarkMode
  );

  useEffect(() => {
    if (localStorage.getItem('prefersDarkMode') == null)
      setDarkMode(prefersDarkMode);
  }, [prefersDarkMode]);

  const handleDarkModeClick = (val) => {
    if (val === 'system') {
      localStorage.removeItem('prefersDarkMode');
    } else if (val === 'light') {
      localStorage.setItem('prefersDarkMode', 'false');
    } else if (val === 'dark') {
      localStorage.setItem('prefersDarkMode', 'true');
    }
  };

  const logoModeTheme = useTheme();
  const logo = logoModeTheme.palette.mode === 'dark' ? LogoDark : LogoLight;

  const avatarChar = email.charAt(0).toUpperCase();
  const avatarColor = stringToColor(avatarChar);

  const logoHeader = (
    <LogoHeader
      component="img"
      src={logo}
      alt="logo"
      onClick={() => navigate('/home/Home')}
      draggable="false"
      sx={{
        paddingLeft: {
          sm: '5px',
          md: '10px',
          lg: '10px',
        },
        display: isMobile && !sidebarOpen ? 'none' : 'block',
        height: '50px',
        width: 'auto',
        cursor: 'pointer',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
      }}
    />
  );

  return (
    <>
      <WelcomeAstrologyDialog
        open={openAstrologyDialog}
        onClose={() => setOpenAstrologyDialog(false)}
      />
      <AuthPopup open={openAuthPopup} onClose={() => setOpenAuthPopup(false)} />
      <Box
        display="flex"
        sx={{
          height: '100vh',
          overflow: 'hidden',
          [theme.breakpoints.down('sm')]: {
            height: '100dvh', // Use dynamic viewport height on mobile
            overflow: 'auto', // Allow scrolling on mobile
            flexDirection: 'row', // Keep row direction but sidebar will be overlay
          },
        }}
      >
        {/* Mobile backdrop */}
        {isMobile && sidebarOpen && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1200,
            }}
            onClick={() => setSidebarOpen(false)}
          />
        )}
        {/* Mobile backdrop */}
        {isMobile && showSettings && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1200,
            }}
            onClick={() => setSidebarOpen(false)}
          />
        )}
        <Sidebar open={sidebarOpen}>
          <Box
            flex={1}
            display="flex"
            flexDirection="column"
            overflow="auto"
            gap={1}
          >
            <IconButton
              onClick={() => setSidebarOpen(!sidebarOpen)}
              sx={{
                width: 30,
                height: 30,
                marginBottom: {
                  xs: '0px',
                  sm: '0px',
                  md: '40px',
                  lg: '45px',
                  xl: '50px',
                },
                marginLeft: {
                  xs: '6px',
                  sm: '6px',
                  lg: '12px',
                },
              }}
            >
              <MenuOutline />
            </IconButton>
            {sidebarOpen && isMobile ? logoHeader : null}
            {sidebarOpen && isMobile ? (
              <Box sx={{ height: '30px' }}></Box>
            ) : null}
            {sidebarOpen ? (
              <NewChatButton
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={isLogin ? handleNewChat : undefined}
                disabled={!isLogin}
                sx={{
                  marginLeft: '18px',
                  cursor: isLogin ? 'pointer' : 'not-allowed',
                  opacity: isLogin ? 1 : 0.5,
                }}
              >
                New chat
              </NewChatButton>
            ) : !isMobile ? (
              <CircularNewChatButton
                onClick={isLogin ? handleNewChat : undefined}
                disabled={!isLogin}
                sx={{
                  marginLeft: {
                    sm: '8px',
                    lg: '18px',
                  },
                  cursor: isLogin ? 'pointer' : 'not-allowed',
                  opacity: isLogin ? 1 : 0.5,
                }}
              >
                <AddIcon fontSize="small" />
              </CircularNewChatButton>
            ) : null}
            <Box
              flex={1}
              sx={{
                display: sidebarOpen ? 'flex' : 'none',
                flexDirection: 'column',
                overflowY: 'auto',
                overflowX: 'hidden',
                width: '100%',
                maxWidth: '100%',
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  marginTop: {
                    xs: '10px',
                    sm: '30px',
                    md: '35px',
                    lg: '40px',
                    xl: '40px',
                  },
                  fontSize: '16px',
                  color: '#BABABA',
                  paddingLeft: '18px',
                }}
              >
                Recent
              </Typography>
              <List
                sx={{
                  pr: '5px',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  width: '100%',
                  maxWidth: '100%',
                  '& .MuiList-root .MuiList-padding': {
                    overflowX: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                }}
              >
                {chatHistory.map((chat) => (
                  <ChatListItem
                    component="div"
                    key={chat.id}
                    onClick={
                      isLogin
                        ? () => {
                            setSelectedChatId(chat.id);
                            if (isMobile) setSidebarOpen(false);
                          }
                        : undefined
                    }
                    disableGutters
                    disablePadding
                    sx={{
                      cursor: isLogin ? 'pointer' : 'not-allowed',
                      '&:hover': isLogin
                        ? { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                        : {},
                      overflowX: 'hidden',
                      width: '100%',
                      maxWidth: '100%',
                      opacity: isLogin ? 1 : 0.5,
                    }}
                  >
                    <ChatListItemIcon sx={{ pl: '18px' }}>
                      <img src={chatboxOutline} width={18} />
                    </ChatListItemIcon>
                    <ListItemText
                      primary={chat.messages[0]?.content || 'Untitled'}
                      slotProps={listItemSlotProps}
                    />
                    <IconButton
                      edge="end"
                      size="small"
                      className="delete-icon"
                      sx={{
                        '&:hover': {
                          backgroundColor: 'transparent',
                        },
                      }}
                      onClick={
                        isLogin
                          ? async (e) => {
                              e.stopPropagation();
                              // Optional: confirm before deleting
                              if (confirm('Delete this chat?')) {
                                await axios
                                  .post(
                                    `${api}/chat/deleteChat/${chat.id}`,
                                    {},
                                    {
                                      headers: {
                                        Authorization: `Bearer ${localStorage.getItem('token')}`,
                                      },
                                    }
                                  )
                                  .then(() => {
                                    posthog.capture('chat_deleted', {
                                      chat_id: chat.id,
                                    });
                                    setChatHistory((prev) =>
                                      prev.filter((c) => c.id !== chat.id)
                                    );
                                    if (selectedChatId === chat.id)
                                      setSelectedChatId(null);
                                  });
                              }
                            }
                          : undefined
                      }
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ChatListItem>
                ))}
              </List>
            </Box>
          </Box>
          <Box
            display={isMobile && !sidebarOpen ? 'none' : 'flex'}
            flexDirection="column"
            gap={1}
            bottom={10}
            flexShrink={0}
          >
            <List>
              <ChatListItem
                component="div"
                disableGutters
                disablePadding
                onClick={isLogin ? () => navigate('/what-to-ask') : undefined}
                sx={{
                  cursor: isLogin ? 'pointer' : 'not-allowed',
                  '&:hover': isLogin
                    ? { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                    : {},
                  mt: { xs: '34px', sm: '34px', md: '34px', lg: '46px' },
                  opacity: isLogin ? 1 : 0.5,
                }}
              >
                <ChatListItemIcon sx={{ pl: '18px' }}>
                  <img src={Idea} width={18} />
                </ChatListItemIcon>
                <ListItemText
                  slotProps={listItemSlotProps}
                  primary={sidebarOpen ? 'Not sure what to ask?' : ''}
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    cursor: 'pointer',
                  }}
                />
              </ChatListItem>
              <ChatListItem
                component="div"
                disableGutters
                disablePadding
                onClick={isLogin ? () => navigate('/chart-detail') : undefined}
                sx={{
                  cursor: isLogin ? 'pointer' : 'not-allowed',
                  '&:hover': isLogin
                    ? { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                    : {},
                  mt: { xs: 0, sm: 0, md: '30px', lg: '30px' },
                  opacity: isLogin ? 1 : 0.5,
                }}
              >
                <ChatListItemIcon sx={{ pl: '18px' }}>
                  <img src={ChartDetailIcon} width={18} />
                </ChatListItemIcon>
                <ListItemText
                  slotProps={listItemSlotProps}
                  primary={sidebarOpen ? 'Chart details' : ''}
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    cursor: 'pointer',
                  }}
                />
              </ChatListItem>
            </List>
          </Box>
        </Sidebar>

        <HowItWorksPopup
          open={exclamationVisible}
          onClose={() => setExclamationVisible(false)}
        />

        <Main>
          {logoHeader}
          <LayoutHeader>
            <TokenInfo>
              <TokenWrapper
                onClick={isLogin ? () => navigate('/pricing') : undefined}
              >
                <Box>
                  <TokenBox elevation={0}>
                    {userTokens}
                    <TokenIconOverlay alt="token" />
                  </TokenBox>
                </Box>
                <Typography
                  sx={{ fontSize: '12px', fontStyle: 'Inter, sans-serif' }}
                >
                  Token{userTokens !== 1 && 's'} left
                </Typography>
              </TokenWrapper>
              <IconButton
                // size="small"
                onClick={() => setExclamationVisible(!exclamationVisible)}
                sx={{
                  width: 24,
                  height: 24,
                  bgcolor:
                    logoModeTheme.palette.mode === 'dark'
                      ? '#454545'
                      : '#BABABA',
                  color: 'white',
                  '&:hover': {
                    bgcolor: '#b0b0b0',
                  },
                  marginTop: '5px',
                  marginLeft: '10px',
                  marginRight: {
                    xs: '10px',
                    sm: '10px',
                    md: '25px',
                    lg: '35px',
                    xl: '35px',
                  },
                }}
              >
                <PriorityHighRoundedIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton
                onClick={isLogin ? handleSettingsClick : undefined}
                sx={{
                  p: 0, // removes default IconButton padding
                  borderRadius: '50%', // keeps it circular
                }}
              >
                <Avatar
                  sx={{
                    backgroundColor: avatarColor,
                    color: '#fff',
                    cursor: 'pointer',
                  }}
                >
                  {avatarChar}
                </Avatar>
              </IconButton>
            </TokenInfo>
          </LayoutHeader>
          <Chat
            messages={messages}
            input={input}
            handleLike={handleLike}
            sendMessage={sendMessage}
            setInput={setInput}
            selectedChatId={selectedChatId}
            setMessages={setMessages}
            handleTokenConfirmation={handleTokenConfirmation}
            isThinking={isThinking}
          />
        </Main>

        <SettingsMenu
          showSettings={showSettings}
          settingsAnchor={settingsAnchor}
          isMobile={isMobile}
          handleCloseSettings={handleCloseSettings}
          navigate={navigate}
          darkMode={darkMode}
          handleDarkModeClick={handleDarkModeClick}
          handleLogout={handleLogout}
          email={email}
          selectedMode={
            localStorageDarkModePreference == null
              ? 'system'
              : localStorageDarkModePreference
                ? 'dark'
                : 'light'
          }
        />
      </Box>
    </>
  );
};

// Styled wrapper to handle hover state
const ChatListItem = styled(ListItem)(() => ({
  px: 1,
  py: 0.5,
  '& .delete-icon': {
    opacity: 0,
    transition: 'opacity 0.2s ease',
  },
  //   '&:hover': {
  //     backgroundColor: 'transparent', // disables hover color
  //   },
  '&:hover .delete-icon': {
    opacity: 1,
  },
}));

const ChatListItemIcon = styled(ListItemIcon)(({ theme }) => ({
  minWidth: 18,
  marginRight: theme.spacing(1),
}));

const NewChatButton = styled(Button)(() => ({
  color: 'gray',
  borderColor: 'lightgray',
  borderRadius: '5px',
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '14px',
  justifyContent: 'flex-start',
  width: 'fit-content',
  '&:hover': {
    backgroundColor: '#f5f5f5',
    borderColor: 'gray',
  },
}));

const CircularNewChatButton = styled(IconButton)(() => ({
  width: 24,
  height: 24,
  borderRadius: '50%',
  border: '2px solid gray',
  color: 'gray',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {
    backgroundColor: '#f5f5f5',
    borderColor: '#888',
  },
}));

const TokenWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  cursor: 'pointer',
});

const TokenBox = styled(Paper)(({ theme }) => ({
  position: 'relative',
  fontFamily: 'Inter, sans-serif',
  padding: '8px 0px',
  borderRadius: '4px',
  backgroundColor: theme.palette.mode === 'dark' ? '#000000' : '#f0f0f0',
  fontWeight: 500,
  fontSize: 18,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '81px',
  height: '35px',
  [theme.breakpoints.down('sm')]: {
    fontSize: 16,
    width: '70px',
    height: '30px',
    padding: '6px 0px',
  },
}));

const TokenIconOverlay = styled(TokenIcon)({
  position: 'absolute',
  left: -9,
  top: '50%',
  transform: 'translateY(-50%)',
  width: 24,
  height: 24,
});

const Sidebar = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: open ? '244px' : '85px',
  transition: 'width 0.1s ease',
  borderTopRightRadius: '50px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  paddingBottom: '18px',
  paddingTop: '18px',
  height: '100vh',
  backgroundColor: theme.palette.mode === 'dark' ? 'black' : '#f7f7f7',
  color: theme.palette.mode === 'dark' ? 'white' : '#8b8b8b',
  [theme.breakpoints.down('lg')]: {
    width: open ? '220px' : '75px',
    paddingBottom: '15px',
    paddingTop: '15px',
  },
  [theme.breakpoints.between('md', 'lg')]: {
    width: open ? '210px' : '72px',
    paddingBottom: '14px',
    paddingTop: '14px',
  },
  [theme.breakpoints.down('md')]: {
    width: open ? '200px' : '70px',
    paddingBottom: '12px',
    paddingTop: '12px',
  },
  [theme.breakpoints.down('sm')]: {
    backgroundColor: open
      ? theme.palette.mode === 'dark'
        ? 'black'
        : '#ffffff'
      : 'transparent',
    width: open ? '90%' : '60px',
    height: open ? '100dvh' : '60px',
    paddingBottom: '8px',
    paddingTop: '8px',
    position: 'fixed',
    zIndex: open ? 1300 : 1100,
    borderTopRightRadius: open ? '0px' : '50px',
    overflowY: open ? 'auto' : 'visible',
    transform: open ? 'translateX(0)' : 'translateX(0)',
  },
}));

const CustomTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} arrow />
))(() => ({
  [`& .MuiTooltip-tooltip`]: {
    backgroundColor: '#E5E5E5',
    fontFamily: 'Inter, sans-serif',
    color: '#454545',
    fontStyle: 'italic',
    fontSize: 12,
    fontWeight: 300,
    borderRadius: 8,
    padding: '12px 16px',
    maxWidth: 300,
  },
}));

const Main = styled(Box)(({ theme }) => ({
  flex: 1,
  paddingTop: '40px',
  paddingLeft: '20px',
  paddingRight: '20px',
  paddingBottom: '10px',
  position: 'relative',
  [theme.breakpoints.down('lg')]: {
    paddingTop: '35px',
    paddingLeft: '18px',
    paddingRight: '18px',
  },
  [theme.breakpoints.between('md', 'lg')]: {
    paddingTop: '32px',
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  [theme.breakpoints.down('md')]: {
    paddingTop: '30px',
    paddingLeft: '15px',
    paddingRight: '15px',
  },
  [theme.breakpoints.down('sm')]: {
    paddingTop: '80px', // Add top padding to account for fixed sidebar
    paddingLeft: '10px',
    paddingRight: '10px',
    marginLeft: '0', // Remove any left margin
    width: '100%', // Take full width
  },
}));

const LogoHeader = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '20px',
  left: '30px',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  [theme.breakpoints.down('md')]: {
    left: '20px',
    top: '15px',
  },
  [theme.breakpoints.down('sm')]: {
    left: '50px', // Move right to avoid collision with sidebar
    top: '10px',
    gap: '8px',
  },
}));

const LayoutHeader = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '20px',
  right: '30px',
  display: 'flex',
  alignItems: 'center',
  gap: '20px',
  [theme.breakpoints.down('md')]: {
    right: '20px',
    gap: '15px',
  },
  [theme.breakpoints.down('sm')]: {
    right: '15px',
    gap: '10px',
  },
}));

const TokenInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
}));

const NewBadge = styled(Badge)(() => ({
  '& .MuiBadge-badge': {
    background: '#000',
    color: '#fff',
    cursor: 'pointer',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: 500,
    marginRight: '5px',
  },
}));

function stringToColor(string) {
  let hash = 0;
  for (let i = 0; i < string.length; i++) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  // Limit hue to bluish range (180°–260°)
  let hue = 180 + (Math.abs(hash) % 80);

  const saturation = 65; // bright but not neon
  const lightness = 55; // keeps it lively

  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
}

export default Layout;
